import React from "react";
import styled from "styled-components";
import {
  breakpoints,
  Text,
  Button as AsurionButton,
} from "@trailhead/asurion-ui";
import { FlexColumnContainer, FlexRowContainer } from "@trailhead/core-ui";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { trackEvent } from "@trailhead/analytics";
import PhoneWithIconsBlue from "../assets/PhoneWithIconsBlue.png";
import { defaultChatPrompts } from ".";
import ExpertChatImage from "../assets/ExpertChatImage.png";

const Container = styled.div`
  width: 100vw;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;

  ${breakpoints.md} {
    background-color: #f0f0f5;
    margin: 0 -100vw;
  }

  ${breakpoints.mdOnly} {
    padding-bottom: 3rem;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1200px;

  ${breakpoints.lg} {
    justify-content: space-between;
  }
`;

const ImageContainer = styled.div`
  width: 50%;
  display: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 2;
  margin: 50px 0px;
  ${breakpoints.lg} {
    display: flex;
  }
`;

const Image = styled.img`
  width: 400px;
  height: 400px;
`;

const ExpertCallContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: start;

  ${breakpoints.md} {
    width: 50%;
  }

  @media (${breakpoints.mdMin}) {
    & > * {
      margin-top: -3rem !important;
    }
  }
`;

const ExpertSetupContainer = styled(FlexColumnContainer)`
  justify-content: space-evenly;
  align-items: center;
  align-self: flex-start;
  flex-direction: column;
  width: 100%;
  padding: 2rem 1rem;
  gap: 20px;
  color: #000000;
  background-color: #fff;
  border: 1px solid #d5d6da;
  border-radius: 5px;
  ${breakpoints.sm} {
    padding: 3rem 1rem;
  }
  ${breakpoints.md} {
    border: none;
    box-shadow: 0px 4px 30px rgba(0, 0, 0, 0.2);
    padding: 3rem 2rem;
    width: 344px;
  }
  ${breakpoints.lg} {
    padding: 4rem 2rem;
    width: 375px;
  }
`;

const HeaderTitle = styled(Text)`
  text-align: center;
`;

const ButtonContainer = styled(FlexRowContainer)`
  justify-content: center;
  flex-wrap: wrap;
`;

const TextWrapper = styled.p`
  text-align: center;
  max-width: 375px;
`;

const CallButtonsTitle = styled(HeaderTitle)`
  margin: 0;
  display: inline-block;
`;

const NoWrapText = styled.span`
  white-space: nowrap;
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  height: auto;
  max-height: 3rem;
  margin: 1% 1%;
  ${breakpoints.sm} {
    max-width: 350px;
  }
`;

const ExpertCallImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExpertCallImageSrc = styled.img`
  max-width: 220px;
  max-height: 90px;
`;

const TagButton = styled(AsurionButtonWrapper)`
  border-color: #a5aaaf;
  padding: 12px 10px;
  &:hover {
    border-color: #868889 !important;
  }
`;

const CallButtonsGroupContainer = styled(FlexColumnContainer)`
  width: 100%;
`;

interface CallButtonProps {
  buttonText: string;
  chatText?: string;
}

const CallButton: React.FC<CallButtonProps> = ({ buttonText }) => {
  const callNumber = "866-314-7861";
  return (
    <TagButton
      size="medium"
      variant="outline"
      onClick={() => {
        window.open(`tel: ${callNumber}`, "_self");
        trackEvent("PathfinderJ_Call_Prompt_Click", { buttonText });
      }}
    >
      {buttonText}
    </TagButton>
  );
};

export const CallPromptsCTA: React.FC = () => {
  const t = useSanitizedTranslation();
  const chatPrompts = defaultChatPrompts;

  return (
    <Container>
      <ContentContainer>
        <ImageContainer>
          <Image src={PhoneWithIconsBlue} />
        </ImageContainer>
        <ExpertCallContainer>
          <ExpertSetupContainer>
            <ExpertCallImageContainer>
              <ExpertCallImageSrc src={ExpertChatImage} />
            </ExpertCallImageContainer>
            <TextWrapper>
              <CallButtonsTitle size={3}>
                Hi there 👋 <NoWrapText>We're your experts.</NoWrapText>
              </CallButtonsTitle>
              <CallButtonsTitle size={3} weight={"heavy"}>
                {t("Select a topic for a quick help.")}
              </CallButtonsTitle>
            </TextWrapper>
            <CallButtonsGroupContainer>
              <ButtonContainer>
                <CallButton
                  buttonText={chatPrompts[0].buttonText}
                  chatText={chatPrompts[0].chatText}
                />
                <CallButton
                  buttonText={chatPrompts[1].buttonText}
                  chatText={chatPrompts[1].chatText}
                />
              </ButtonContainer>
              <ButtonContainer>
                <CallButton
                  buttonText={chatPrompts[2].buttonText}
                  chatText={chatPrompts[2].chatText}
                />
                <CallButton
                  buttonText={chatPrompts[3].buttonText}
                  chatText={chatPrompts[3].chatText}
                />
              </ButtonContainer>
              <ButtonContainer>
                <CallButton buttonText={t("Help with something else")} />
              </ButtonContainer>
            </CallButtonsGroupContainer>
          </ExpertSetupContainer>
        </ExpertCallContainer>
      </ContentContainer>
    </Container>
  );
};
